import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import { useReactToPrint } from 'react-to-print';
import { faFilePdf, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { showMessage } from '../../../helpers/message';
import infracoes_api from '../../../api/infracoes_api';

// Components
import renderizarItem from './_components/renderizarItem';
 
const pageStyle = `
@page { size: auto; margin: 0mm; }
@media print { body { -webkit-print-color-adjust: exact; } }
@media print {
  @page { size:A4; margin:0; }
  .relatorio { width:100vw!important; display:flex; flex-direction:column; }
  .noPrint { display:none; }
  .impressaoCompletaContainer > *:not(:first-child) { page-break-before: always; margin-top: 0!important; }
  /* A imagem da página do PDF tem proporção A4: somada ao cabeçalho do CartaoFicha ela estoura a
     folha e é empurrada para a próxima, deixando o título sozinho na anterior. */
  .paginaPdfCompleta { max-height: 22cm; width: auto; }
  /* O rodape do documento (numero do auto + data de extracao) e o ultimo bloco do fluxo: quando o
     conteudo enchia a folha, ele nao cabia e ia sozinho para a folha seguinte, que saia quase
     vazia. Como rodape de tabela ele fica no pe da folha do proprio item, sem gerar folha nova. */
  .documentoImpressao { display: table; width: 100%; }
  .rodapeDocumento { display: table-footer-group; }
}
`;

const estiloEspacamentoItens = `
.impressaoCompletaContainer > *:not(:first-child) { margin-top: 1.5rem; }
`;

//Teto de espera antes de liberar a impressão mesmo com item pendente. Generoso de propósito: os
//itens de CTR e ROC dependem de sistema externo, cujo timeout no backend é de 2 minutos.
const TETO_CARREGAMENTO_MS = 30000;
 
export default function FichaImpressaoCompleta() {
    const location = useLocation();
    const hash = new URLSearchParams(location.search).get('hash');

    const [infracao, setInfracao] = useState(null);
    const [itens, setItens] = useState([]);
    const [prontos, setProntos] = useState(new Set());
    const [itensCarregados, setItensCarregados] = useState(false);
    const [tempoEsgotado, setTempoEsgotado] = useState(false);

    const ref = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => ref.current,
        documentTitle: `ImpressaoCompleta_${infracao?.numeroInfracao ?? hash}`,
        pageStyle
    });

    useEffect(() => {
        if (!hash) return;

        infracoes_api.GetItemFiscalizacaoHash(hash)
            .then(({ data }) => setInfracao(data))
            .catch(error => showMessage("Erro", error?.message ?? "Erro na operação!", "error", null));

        infracoes_api.GetImpressaoItensDisponiveis(hash)
            .then(({ data }) => setItens(data?.itensCompleta ?? []))
            .catch(() => setItens([]))
            .finally(() => setItensCarregados(true));
    }, [hash]);

    const marcarPronto = (parametrizacaoHash) => () => {
        setProntos(prev => new Set(prev).add(parametrizacaoHash));
    };

    const todosSinalizaram = useMemo(
        () => itensCarregados && itens.every(item => prontos.has(item.hash)),
        [itensCarregados, itens, prontos]
    );

    //Um item que nunca sinaliza pronto (onLoad de iframe que não dispara, consulta que falhou)
    //deixava o botão em "Carregando..." para sempre, sem forma de imprimir o resto. Depois do teto
    //o botão libera assim mesmo, avisando que pode faltar página.
    useEffect(() => {
        if (!itensCarregados || todosSinalizaram)
            return;

        const temporizador = setTimeout(() => setTempoEsgotado(true), TETO_CARREGAMENTO_MS);
        return () => clearTimeout(temporizador);
    }, [itensCarregados, todosSinalizaram]);

    const tudoPronto = todosSinalizaram || tempoEsgotado;

    const itensPendentes = itens.filter(item => !prontos.has(item.hash));

    if (!infracao) return <></>;

    return (
        <div>
            <style>{estiloEspacamentoItens}</style>

            <div className="noPrint p-3 d-flex justify-content-end align-items-center gap-3 border-bottom">
                {tempoEsgotado && itensPendentes.length > 0 && (
                    <span className="text-warning">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                        {itensPendentes.length === 1
                            ? "1 item não terminou de carregar e pode sair em branco na impressão."
                            : `${itensPendentes.length} itens não terminaram de carregar e podem sair em branco na impressão.`}
                    </span>
                )}
                <Button color="danger" disabled={!tudoPronto} onClick={handlePrint}>
                    <FontAwesomeIcon icon={faFilePdf} /> {tudoPronto ? "Imprimir" : "Carregando..."}
                </Button>
            </div>

            <div ref={ref} className="impressaoCompletaContainer">
                {itens.map((item) => (
                    <React.Fragment key={item.hash}>
                        {renderizarItem(item, infracao, hash, marcarPronto(item.hash))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
